// src/pages/SectionManage.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Grid, Card, CardContent,
    CardActionArea, Button, CircularProgress
} from '@mui/material';
import {
    Info, EmojiEvents, Edit, Image, ContactMail, KeyboardArrowLeft
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import AdminHeader from '../components/AdminHeader';

const SectionManage = () => {
    const { user, isAuthenticated, loading } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && (!isAuthenticated || user?.role !== 'ADMIN')) {
            navigate('/login');
        }
    }, [isAuthenticated, user, loading, navigate]);

    const sections = [
        {
            title: 'За нас',
            description: 'Уреди ја секцијата "За нас" на почетната страна',
            icon: <Info sx={{ fontSize: 60 }} />,
            color: '#2196F3',
            bgColor: isDarkMode ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)',
            onClick: () => navigate('/admin/sections/about-us')
        },
        {
            title: 'Достигнувања',
            description: 'Уреди ја секцијата "Достигнувања" и прикажи успеси',
            icon: <EmojiEvents sx={{ fontSize: 60 }} />,
            color: '#FFA726',
            bgColor: isDarkMode ? 'rgba(255, 167, 38, 0.15)' : 'rgba(255, 167, 38, 0.1)',
            onClick: () => navigate('/admin/sections/achievements')
        },
        {
            title: 'Слики на почетна',
            description: 'Управувај со сликите прикажани на почетната страна',
            icon: <Image sx={{ fontSize: 60 }} />,
            color: '#E91E63',
            bgColor: isDarkMode ? 'rgba(233, 30, 99, 0.15)' : 'rgba(233, 30, 99, 0.1)',
            onClick: () => navigate('/admin/sections/front-page')
        },
        {
            title: 'Контакти',
            description: 'Уреди ги контакт информациите и адресата',
            icon: <ContactMail sx={{ fontSize: 60 }} />,
            color: '#4CAF50',
            bgColor: isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
            onClick: () => navigate('/admin/sections/contacts')
        }
    ];

    // ===== LOADING =====
    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                backgroundColor: 'background.default',
                display: 'flex', justifyContent: 'center', alignItems: 'center'
            }}>
                <CircularProgress sx={{ color: '#ff7eb9' }} />
                <Typography variant="h6" sx={{ ml: 2 }}>Вчитување...</Typography>
            </Box>
        );
    }

    if (!isAuthenticated || user?.role !== 'ADMIN') return null;

    return (
        <Box sx={{
            py: 4, minHeight: '100vh',
            backgroundColor: 'background.default',
            transition: 'background-color 0.3s ease'
        }}>
            <Container maxWidth="lg">
                <AdminHeader />

                {/* ✅ NAZAD KON ADMIN PANEL kopce */}
                <Box sx={{ mb: 4 }}>
                    <Button
                        onClick={() => navigate('/admin')}
                        startIcon={<KeyboardArrowLeft sx={{ fontSize: '28px !important' }} />}
                        variant="outlined"
                        sx={{
                            color: '#ff7eb9',
                            borderColor: '#ff7eb9',
                            backgroundColor: 'rgba(255,126,185,0.06)',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            px: 3, py: 1.5,
                            borderRadius: '12px',
                            textTransform: 'none',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#ff7eb9',
                                color: 'white',
                                borderColor: '#ff4a97',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(255,126,185,0.3)',
                            }
                        }}
                    >
                        Назад кон Admin Панел
                    </Button>
                </Box>

                {/* Header */}
                <Box sx={{ mb: 6, textAlign: 'center' }}>
                    <Typography variant="h2" sx={{
                        color: 'primary.main', fontWeight: 'bold', mb: 2,
                        textShadow: isDarkMode ? '0 0 20px rgba(255,126,185,0.5)' : 'none'
                    }}>
                        📝 Управување со секции
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Изберете која секција сакате да ја уредите
                    </Typography>
                </Box>

                {/* Section Cards */}
                <Grid container spacing={4} justifyContent="center">
                    {sections.map((section, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Card sx={{
                                height: '100%',
                                transition: 'all 0.3s ease',
                                border: `3px solid ${section.color}30`,
                                borderRadius: 4,
                                bgcolor: 'background.paper',
                                '&:hover': {
                                    transform: 'translateY(-12px)',
                                    boxShadow: isDarkMode
                                        ? `0 16px 48px ${section.color}50`
                                        : `0 16px 48px ${section.color}40`,
                                    borderColor: section.color,
                                }
                            }}>
                                <CardActionArea onClick={section.onClick} sx={{ height: '100%' }}>
                                    <CardContent sx={{ p: 5, textAlign: 'center' }}>
                                        {/* Icon */}
                                        <Box sx={{
                                            width: 120, height: 120, borderRadius: 4,
                                            backgroundColor: section.bgColor,
                                            display: 'flex', alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 24px auto',
                                            color: section.color,
                                            border: `3px solid ${section.color}40`,
                                            position: 'relative',
                                            transition: 'all 0.3s ease',
                                        }}>
                                            {section.icon}
                                            {/* Edit Badge */}
                                            <Box sx={{
                                                position: 'absolute', bottom: -12, right: -12,
                                                bgcolor: section.color, color: 'white',
                                                width: 48, height: 48, borderRadius: '50%',
                                                display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', boxShadow: 3
                                            }}>
                                                <Edit sx={{ fontSize: 24 }} />
                                            </Box>
                                        </Box>

                                        {/* Title */}
                                        <Typography variant="h3" sx={{
                                            fontWeight: 'bold', color: section.color, mb: 2
                                        }}>
                                            {section.title}
                                        </Typography>

                                        {/* Description */}
                                        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                                            {section.description}
                                        </Typography>

                                        {/* Action Text */}
                                        <Box sx={{
                                            display: 'inline-flex', alignItems: 'center',
                                            gap: 1, px: 3, py: 1.5, borderRadius: 3,
                                            bgcolor: section.bgColor, color: section.color,
                                            fontWeight: 'bold',
                                            border: `2px solid ${section.color}30`
                                        }}>
                                            <Edit />
                                            Уреди секција
                                        </Box>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Info Box */}
                <Box sx={{
                    mt: 6, p: 4,
                    bgcolor: isDarkMode ? 'rgba(255,126,185,0.1)' : 'rgba(255,126,185,0.05)',
                    borderRadius: 3, border: '2px solid', borderColor: 'primary.main',
                    textAlign: 'center'
                }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
                        💡 Совет
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Овие секции се прикажуваат на почетната страна на вашиот сајт.
                        Редовно ажурирајте ги за да ги информирате корисниците за новости и достигнувања.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default SectionManage;